<template>
	<div class="prod-details-page">
		<div class="top-content" :class="{ 'wide-prod-img': wideProdImg }">
			<div class="content-inner">
				<back-button :back-url="props.backUrl" />
				<div class="prod-img-name-wrap">
					<div class="img-wrap">
						<div class="placeholder" v-if="modelLine.video && wideProdImg"></div>
						<slot name="modelImg" />
						<play-button class="dark-border small" v-if="modelLine.video && wideProdImg" @playBtnClicked="playVideo(modelLine.video)">
							<template #label>{{ $t('common.watchInAction') }}</template>
						</play-button>
					</div>
					<div class="model-line-name">
						<product-lines :line="props.prodLine" />
						<h1 v-if="slots['modelName']">
							<slot name="modelName" />
						</h1>
						<h2 v-if="slots['modelSubHeadline']">
							<slot name="modelSubHeadline" />
						</h2>
						<play-button class="small" v-if="modelLine.video && !wideProdImg" @playBtnClicked="playVideo(modelLine.video)">
							<template #label>{{ $t('common.watchInAction') }}</template>
						</play-button>
					</div>
				</div>
			</div>

			<div class="curve-border dir-down dots">
				<dots-background />
			</div>
		</div>

		<div class="cta-section">
			<div class="content-inner">
				<h2>
					<template v-if="slots['modelTagline1']">
						<slot name="modelTagline1" /><br>
					</template>
					<template v-if="slots['modelTagline2']">
						<slot name="modelTagline2" />
					</template>
				</h2>
				<p v-if="slots['modelDescription']">
					<slot name="modelDescription" />
				</p>
				<div>
					<router-link v-if="store.settings.contact.method === 'form'"
						class="cta-btn"
						:to="`/Contact?referrer=${props.prodLine}${ slots['modelName'] ? ' | ' + slots.modelName()[0].children : ''}`"
					>
						{{ $t('common.inquireNow') }}
						<img src="@/images/icons/arrows-right.png" alt="">
					</router-link>
					<contact-qr v-else :url="store.contactUrl" />
				</div>
			</div>

			<div class="curve-border dir-down green-pattern"></div>
		</div>

		<div class="spec-section">
			<div class="specs-header" v-if="slides.length > 1">
				<button type="button" class="carousel-nav" :class="{ clicked: clickedBtn === 'prev' }" @click="carouselPrev" @animationend="clickedBtn = ''">
					<img src="@/images/icons/arrows-left.png" :alt="$t('common.previous')">
					<span>{{ prevLabel }}</span>
				</button>
				<button type="button" class="carousel-nav" :class="{ clicked: clickedBtn === 'next' }" @click="carouselNext" @animationend="clickedBtn = ''">
					<span>{{ nextLabel }}</span>
					<img src="@/images/icons/arrows-right.png" :alt="$t('common.next')">
				</button>
			</div>

			<Carousel class="details-carousel" ref="detailsCarousel" :settings="settings" v-if="slides.length" @update:modelValue="onCarouselUpdate">
				<Slide v-for="(s, i) in carouselSlides" :key="i">
					<div v-if="s === 'features'" class="features-slide slide">
						<h2>{{ $t('common.features') }}</h2>
						<ul>
							<li v-for="(feature, i) in props.modelLine.features" :key="i">
								<svg class="icon">
									<use :href="`#${feature.iconId}`"></use>
								</svg>
								<div class="feature-text" v-html="feature.text"></div>
							</li>
							<li v-if="(props.modelLine.features.length) % 2"></li>
						</ul>
					</div>
					<div v-else-if="s === 'specs'" class="specs-slide slide">
						<h2>{{ $t('common.specs') }}</h2>
						<div class="select-contain">
							<div class="input-contain" :class="{ disabled: props.modelLine.models.length === 1 }">
								<select :disabled="props.modelLine.models.length === 1" @change="selectedModel = $event.target.value">
									<option v-for="model in props.modelLine.models" :value="model.modelNum" :key="model.modelNum">{{ model.modelNum }}</option>
								</select>
								<label>{{ $t('common.model') }} <span>{{ selectedModel }}</span></label>
							</div>
						</div>
						<ul :class="{ 'full-width': props.specsFullWidth }">
							<template v-for="(spec, i) in selectedModelData.modelSpecs" :key="i">
								<li>
									<div class="spec-label">{{ selectedModelData.specLabels[i] }}</div>
									<div class="spec" v-if="(spec instanceof Measurement)">
										{{  formatMeasurementValue(spec.value) }}
										<span class="units">{{ formatMeasurementUnit(spec.units) }}</span>
									</div>
									<div class="spec" v-else v-html="spec"></div>
								</li>
							</template>
						</ul>
					</div>
					<div v-else-if="s === 'options'" class="options-slide slide">
						<h2>{{ $t('common.options') }}</h2>
						<ul :class="{ 'full-width': props.modelLine.options.length <= 5 }">
							<li v-for="(option, i) in props.modelLine.options" :key="i">
								<img src="@/images/icons/check.png" alt="Check">
								<span class="option-text">
									<span>{{ option.text || option }}</span>
									<button v-if="option.video" type="button" class="option-video" @click="playVideo(option.video)">
										<svg viewBox="0 0 12 14" aria-hidden="true"><path d="M0 0 12 7 0 14Z" /></svg>
										{{ $t('common.watchVideo') }}
									</button>
								</span>
							</li>
							<li v-if="(props.modelLine.options.length) % 2"></li>
						</ul>
					</div>
				</Slide>
			</Carousel>
		</div>

		<template v-if="activeVideo">
			<transition name="toggle-fade">
				<div v-show="open" class="modal-bg" @click="closeVideo"></div>
			</transition>

			<transition name="toggle-scale">
				<video v-show="open" class="modal-video" ref="popupVid" :src="`/videos3/${activeVideo}`" crossorigin="anonymous" playsinline controls preload="auto" @ended="closeVideo"></video>
			</transition>
		</template>
	</div>
</template>

<script setup>
import { formatMeasurementUnit, formatMeasurementValue } from '@/helpers/measurementFormatting';
import Measurement from '@/models/Measurement';
import { useStore } from '@/store';
import ContactQr from '@/views/_partials/ContactQr.vue';
import { computed, nextTick, onMounted, onUnmounted, ref, toRef, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';
import { Carousel, Slide } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';

const props = defineProps({
	prodLine: {
		type: String,
		required: true,
	},
	backUrl: {
		type: String,
		required: false,
		default: '/'
	},
	modelLine: {
		type: Object,
		required: true,
	},
	wideProdImg: {
		type: Boolean,
		required: false,
	},
	specsFullWidth: {
		type: Boolean,
		required: false,
	}
})

const { t } = useI18n();
const store = useStore();
const open = toRef(store, 'videoPopupOpen');
const popupVid = ref(null);
const activeVideo = ref(props.modelLine.video);
const slots = useSlots();

const hasFeatures = computed(() => Array.isArray(props.modelLine.features) && props.modelLine.features.length > 0);
const hasOptions = computed(() => Array.isArray(props.modelLine.options) && props.modelLine.options.length > 0);
const hasSpecs = computed(() => Array.isArray(props.modelLine.models) && props.modelLine.models.length > 0);

const slides = computed(() => {
	const list = [];
	if (hasFeatures.value) list.push('features');
	if (hasSpecs.value) list.push('specs');
	if (hasOptions.value) list.push('options');
	return list;
});

const carouselSlides = computed(() => {
	const len = slides.value.length;
	if (len === 2) {
		// Create a 4-item sequence to preserve directionality and avoid identical adjacency at wrap boundaries
		return [slides.value[0], slides.value[1], slides.value[0], slides.value[1]];
	}
	return slides.value;
});

const selectedModel = ref(hasSpecs.value ? props.modelLine.models[0].modelNum : '');
const selectedModelData = computed(() => {
	if (!hasSpecs.value) {
		return { modelSpecs: [], specLabels: [] };
	}
	let model = props.modelLine.models.find(x => x.modelNum === selectedModel.value) || props.modelLine.models[0];
	if (!model) {
		return { modelSpecs: [], specLabels: [] };
	}
	let emptyValues = model.modelSpecs.reduce((acc, curr, index) => {
		if (curr === '-') {
			acc.push(index);
		}
		return acc;
	}, []);
	model.modelSpecs = model.modelSpecs.filter((x, i) => !emptyValues.includes(i));
	const labels = Array.isArray(props.modelLine.labels) ? props.modelLine.labels : [];
	model.specLabels = labels.filter((x, i) => !emptyValues.includes(i));
	return model;
});

const detailsCarousel = ref(null);
const clickedBtn = ref('');
// Track the actual carousel index (over carouselSlides), not the logical slide index
const carouselIndex = ref(0);
function onCarouselUpdate(val) {
	carouselIndex.value = val;
}
const settings = {
	itemsToShow: 1,
	transition: 500,
	wrapAround: true,
	mouseDrag: false,
	touchDrag: false,
};

const carouselNavLabels = ref([]);
const lastNavAt = ref(0);

function labelizeSlide(key) {
	if (key === 'features') return t('common.features');
	if (key === 'specs') return t('common.specs');
	if (key === 'options') return t('common.options');
	return '';
}

function carouselPrev() {
	if (!detailsCarousel.value || slides.value.length === 0) return;
	const now = Date.now();
	if (now - lastNavAt.value < settings.transition) return;
	lastNavAt.value = now;
	detailsCarousel.value.prev();
	clickedBtn.value = 'prev';
}

function carouselNext() {
	if (!detailsCarousel.value || slides.value.length === 0) return;
	const now = Date.now();
	if (now - lastNavAt.value < settings.transition) return;
	lastNavAt.value = now;
	detailsCarousel.value.next();
	clickedBtn.value = 'next';
}

// Determine the logical current slide key from the real carousel index
const currentSlideKey = computed(() => {
	const list = carouselSlides.value;
	if (!Array.isArray(list) || list.length === 0) return '';
	if (slides.value.length === 2) {
		// Map back to the logical slide using modulo 2
		return slides.value[carouselIndex.value % 2];
	}
	return list[carouselIndex.value % list.length];
});

const prevLabel = computed(() => {
	const len = slides.value.length;
	if (len < 2) return '';
	const logicalIndex = slides.value.indexOf(currentSlideKey.value);
	const idx = (logicalIndex - 1 + len) % len;
	return labelizeSlide(slides.value[idx]);
});

const nextLabel = computed(() => {
	const len = slides.value.length;
	if (len < 2) return '';
	const logicalIndex = slides.value.indexOf(currentSlideKey.value);
	const idx = (logicalIndex + 1) % len;
	return labelizeSlide(slides.value[idx]);
});

async function playVideo(video = props.modelLine.video) {
	activeVideo.value = video;
	open.value = true;
	await nextTick();
	try {
		await popupVid.value.play();
	} catch (error) {
		if (error.name !== 'AbortError') console.warn('Failed to play video:', error);
	}
}

function closeVideo() {
	open.value = false;
	popupVid.value.pause();
	popupVid.value.currentTime = 0.0;
}

onUnmounted(() => { open.value = false; });

onMounted(() => {
	if (props.modelLine.video) {
		popupVid.value.load();
	}
	// Initialize nav labels in case slide availability changed
	carouselNavLabels.value = slides.value.map(s => labelizeSlide(s));
})
</script>
